import { useEffect, useState } from "react";

import { Folder } from "@fronterahealth/frontera-ui-components";

import {
  ApiGoalBankFolderFolderTypeChoices,
  GoalBankFolderTypeEnum,
  useCreateGoalBankFolderMutation,
  useDeleteGoalBankFolderMutation,
  useGoalBankFoldersQuery,
  useUpdateGoalBankFolderMutation,
} from "@api/graphql/types-and-hooks";
import { convertDBString } from "@components/forms/utils";
import { notifySuccess } from "@components/notifications/notifications";

export interface FolderWithCurriculum extends Folder {
  curriculumId?: string;
  curriculumName?: string;
}

// Order folders like "Adaptive | 3 Month" by domain, then unit (Week < Month < Year), then number,
// so all Months come before all Years and numbers run 1,2,3,…,10 (not 1,10,11,2 and not 3-clumped).
const UNIT_RANK: Record<string, number> = { week: 0, month: 1, year: 2 };
const AGE_UNIT_RE = /week|month|year/i;
const AGE_NUM_RE = /\d+/;
const parseFolderAge = (name: string): { unit: number; num: number } | null => {
  const unitMatch = AGE_UNIT_RE.exec(name);
  const numMatch = AGE_NUM_RE.exec(name);
  if (unitMatch === null || numMatch === null) return null;
  return { unit: UNIT_RANK[unitMatch[0].toLowerCase()] ?? 99, num: Number.parseInt(numMatch[0], 10) };
};
const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
const byFolderName = (a: { name?: string }, b: { name?: string }) => {
  const an = a.name ?? "";
  const bn = b.name ?? "";
  const prefixCmp = natural(an.split("|")[0].trim(), bn.split("|")[0].trim());
  if (prefixCmp !== 0) return prefixCmp;
  const ageA = parseFolderAge(an);
  const ageB = parseFolderAge(bn);
  if (ageA && ageB) {
    if (ageA.unit !== ageB.unit) return ageA.unit - ageB.unit;
    if (ageA.num !== ageB.num) return ageA.num - ageB.num;
  }
  return natural(an, bn);
};

export const useGoalBankFolders = (searchTerm?: string, usaGoalsCount?: boolean) => {
  const { data, isFetched, refetch, isRefetching } = useGoalBankFoldersQuery({ searchTerm });
  const [userFolders, setUserFolders] = useState<Folder[]>([]);
  const [orgFolders, setOrgFolders] = useState<FolderWithCurriculum[]>([]);
  const createFolder = useCreateGoalBankFolderMutation({});
  const deleteFolderMutation = useDeleteGoalBankFolderMutation({});
  const updateGoalBankFolderMutation = useUpdateGoalBankFolderMutation({});

  useEffect(() => {
    setUserFolders(
      (
        data?.getGoalBankFolders?.userFolders?.map((folder) => {
          return {
            name: folder?.name,
            id: folder?.id,
            type: folder?.folderType,
            count: usaGoalsCount ? folder?.goalsCount : folder?.targetsCount,
          } as Folder;
        }) ?? []
      ).sort(byFolderName),
    );

    setOrgFolders(
      (
        data?.getGoalBankFolders?.organizationFolders?.map((folder) => {
          return {
            name: folder?.name,
            id: folder?.id,
            type: folder?.folderType,
            count: usaGoalsCount ? folder?.goalsCount : folder?.targetsCount,
            curriculumId: folder?.curriculum?.id ?? undefined,
            curriculumName: folder?.curriculum?.name ?? undefined,
          } as FolderWithCurriculum;
        }) ?? []
      ).sort(byFolderName),
    );
  }, [isFetched, isRefetching, usaGoalsCount]);

  const createNewFolder = async (folderName: string, folderType: string): Promise<Folder> => {
    const response = await createFolder.mutateAsync({
      input: {
        folderType: folderType === "personal" ? GoalBankFolderTypeEnum.User : GoalBankFolderTypeEnum.Organization,
        name: folderName,
      },
    });
    notifySuccess("Successfully Created Folder");
    const newFolder = {
      id: response.createGoalBankFolder?.goalBankFolder?.id ?? "",
      name: response.createGoalBankFolder?.goalBankFolder?.name ?? "",
      type: response.createGoalBankFolder?.goalBankFolder?.folderType ?? "",
      count: 0,
    };
    if (newFolder.type === ApiGoalBankFolderFolderTypeChoices.Organization) {
      setOrgFolders((prev) => [...prev, newFolder]);
    } else if (newFolder.type === ApiGoalBankFolderFolderTypeChoices.User) {
      setUserFolders((prev) => [...prev, newFolder]);
    }
    refetch();
    return newFolder as Folder;
  };

  const deleteFolder = async (folder: Folder) => {
    await deleteFolderMutation.mutateAsync(
      {
        input: {
          folderId: folder.id,
          folderType: convertDBString(folder.type) as GoalBankFolderTypeEnum,
        },
      },
      {
        onSuccess: () => {
          notifySuccess("Folder Deleted");
          refetch();
        },
      },
    );
  };

  const editFolder = async (folder: Folder, newValue: string) => {
    if (newValue && folder.name != newValue)
      await updateGoalBankFolderMutation.mutateAsync(
        {
          input: {
            folderId: folder.id,
            name: newValue,
            folderType: convertDBString(folder.type) as GoalBankFolderTypeEnum,
          },
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
  };

  return {
    userFolders,
    orgFolders,
    createNewFolder,
    deleteFolder,
    editFolder,
  };
};
